/**
 * QuickAddCertDialog — Schnelles Anlegen einer Weiterbildung oder Qualifikation
 * direkt aus der Mitarbeiter-Detailansicht heraus (ohne Umweg ueber „Bearbeiten").
 *
 * Modes:
 *   - kind="training"      → Felder: Titel, Anbieter, Datum, Gueltig bis, Stunden, Beschreibung
 *   - kind="qualification" → Felder: Bezeichnung, Institution, Erworben, Gueltig bis
 *
 * Beide Modi unterstuetzen optionalen Datei-Upload (PDF/JPG/PNG, max. 10 MB).
 * Bei Erfolg wird `employeesAPI.update(id, {trainings|qualifications: [...]})`
 * aufgerufen und `onSuccess()` getriggert.
 */
import React, { useState, useMemo } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { DatePickerDE } from './ui/date-picker-de';
import { FileDropzone } from './ui/file-dropzone';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { employeesAPI, uploadAPI } from '../lib/api';

export const QuickAddCertDialog = ({ open, onOpenChange, kind, employee, onSuccess }) => {
  const isTraining = kind === 'training';
  const label = isTraining ? 'Weiterbildung' : 'Qualifikation';

  const blank = useMemo(() => (isTraining
    ? { title: '', provider: '', date: '', expiry_date: '', hours: '', description: '',
        certificate_url: '', certificate_filename: '' }
    : { name: '', institution: '', date: '', expiry_date: '',
        document_url: '', document_filename: '' }), [isTraining]);

  const [form, setForm] = useState(blank);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  React.useEffect(() => { if (open) setForm(blank); }, [open, blank]);

  const update = (key, value) => setForm((p) => ({ ...p, [key]: value }));

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const empName = `${employee.last_name || ''}_${employee.first_name || ''}`.trim();
      const res = await uploadAPI.uploadCertificate(file, {
        employee_name: empName,
        document_title: isTraining ? (form.title || '') : (form.name || ''),
        document_date: form.date || '',
      });
      if (isTraining) {
        update('certificate_url', res.data.url);
        update('certificate_filename', res.data.original_filename);
      } else {
        update('document_url', res.data.url);
        update('document_filename', res.data.original_filename);
      }
      toast.success('Datei hochgeladen');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload fehlgeschlagen');
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    const titleField = isTraining ? form.title : form.name;
    if (!titleField?.trim()) {
      toast.error(isTraining ? 'Titel ist Pflicht' : 'Bezeichnung ist Pflicht');
      return;
    }
    setSaving(true);
    try {
      const nowIso = new Date().toISOString();
      const newEntry = isTraining ? {
        id: `tr-${Date.now()}`,
        title: form.title.trim(),
        provider: form.provider?.trim() || null,
        date: form.date || nowIso.slice(0, 10),
        expiry_date: form.expiry_date || null,
        hours: form.hours ? Number(form.hours) : null,
        description: form.description?.trim() || null,
        certificate_url: form.certificate_url || null,
        certificate_filename: form.certificate_filename || null,
        created_at: nowIso,
      } : {
        id: `q-${Date.now()}`,
        name: form.name.trim(),
        institution: form.institution?.trim() || null,
        date: form.date || null,
        expiry_date: form.expiry_date || null,
        document_url: form.document_url || null,
        document_filename: form.document_filename || null,
      };

      const key = isTraining ? 'trainings' : 'qualifications';
      const existing = employee[key] || [];

      // PUT /employees erwartet das komplette Employee-Schema (EmployeeCreate),
      // nicht nur Partial-Update. Wir senden den geladenen Mitarbeiter zurueck
      // und ueberschreiben nur das geaenderte Array.
      // Computed/Server-Felder rausfiltern, damit Pydantic-Validation sauber laeuft.
      const {
        id: _id, _id: _mongoId, created_at, updated_at,
        location_name, location_short_id, department_name, branch_name,
        ...payload
      } = employee;
      payload[key] = [...existing, newEntry];

      await employeesAPI.update(employee.id, payload);
      toast.success(`${label} hinzugefügt`);
      onOpenChange(false);
      onSuccess?.();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      let msg = 'Speichern fehlgeschlagen';
      if (typeof detail === 'string') {
        msg = detail;
      } else if (Array.isArray(detail) && detail.length > 0) {
        // FastAPI/Pydantic 422 returns an array of {loc, msg, type, ...}
        msg = detail.map((d) => d?.msg || JSON.stringify(d)).join('; ');
      }
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const uploadedFilename = isTraining ? form.certificate_filename : form.document_filename;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-lg max-h-[90vh] overflow-y-auto"
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>Neue {label} hinzufügen</DialogTitle>
          <DialogDescription>
            Für {employee.first_name} {employee.last_name}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {isTraining ? (
            <>
              <FormField label="Titel der Weiterbildung *" testId="quick-add-title">
                <Input value={form.title} onChange={(e) => update('title', e.target.value)}
                  data-testid="quick-add-training-title" />
              </FormField>
              <FormField label="Anbieter / Veranstalter">
                <Input value={form.provider} onChange={(e) => update('provider', e.target.value)}
                  data-testid="quick-add-training-provider" />
              </FormField>
              <div className="grid grid-cols-2 gap-3">
                <FormField label="Abgeschlossen am">
                  <DatePickerDE value={form.date} onChange={(v) => update('date', v)} />
                </FormField>
                <FormField label="Gültig bis (optional)">
                  <DatePickerDE value={form.expiry_date} onChange={(v) => update('expiry_date', v)} />
                </FormField>
              </div>
              <FormField label="Umfang (Stunden)">
                <Input type="number" value={form.hours} onChange={(e) => update('hours', e.target.value)}
                  data-testid="quick-add-training-hours" />
              </FormField>
              <FormField label="Beschreibung (optional)">
                <Textarea rows={2} value={form.description}
                  onChange={(e) => update('description', e.target.value)} />
              </FormField>
            </>
          ) : (
            <>
              <FormField label="Bezeichnung *">
                <Input value={form.name} onChange={(e) => update('name', e.target.value)}
                  data-testid="quick-add-qual-name" />
              </FormField>
              <FormField label="Institution / Aussteller">
                <Input value={form.institution} onChange={(e) => update('institution', e.target.value)}
                  data-testid="quick-add-qual-institution" />
              </FormField>
              <div className="grid grid-cols-2 gap-3">
                <FormField label="Erworben am">
                  <DatePickerDE value={form.date} onChange={(v) => update('date', v)} />
                </FormField>
                <FormField label="Gültig bis (optional)">
                  <DatePickerDE value={form.expiry_date} onChange={(v) => update('expiry_date', v)} />
                </FormField>
              </div>
            </>
          )}

          <FormField label="Nachweis hochladen (optional, PDF/JPG/PNG, max. 10 MB)">
            <FileDropzone
              accept=".pdf,.jpg,.jpeg,.png"
              onUpload={handleUpload}
              uploading={uploading}
              filename={uploadedFilename}
              testId="quick-add-file-dropzone"
            />
          </FormField>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Abbrechen
          </Button>
          <Button onClick={handleSave} disabled={saving || uploading} data-testid="quick-add-submit">
            {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            {label} speichern
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const FormField = ({ label, children }) => (
  <div className="space-y-1">
    <Label>{label}</Label>
    {children}
  </div>
);

export default QuickAddCertDialog;
